import { useState, useEffect } from 'react';

interface TimeSlot {
  start_time: string;
  end_time: string;
}

interface BookingCalendarProps {
  timeslotsApi: string;
  appointmentsApi: string;
  texts: {
    heading?: string;
    selectDate?: string;
    selectTime?: string;
    reviewBooking?: string;
    customerName?: string;
    customerPhone?: string;
    notes?: string;
    submit?: string;
    modifyTime?: string;
    back?: string;
    next?: string;
    noSlotsAvailable?: string;
    bookingSuccess?: string;
    bookingError?: string;
    selectedDate?: string;
    selectedTime?: string;
    monthNames?: string[];
    dayNames?: string[];
  };
  containerStyle?: string;
}

export default function BookingCalendar(props: BookingCalendarProps) {
  const {
    timeslotsApi,
    appointmentsApi,
    texts,
    containerStyle = ''
  } = props;

  const monthNames = texts.monthNames || ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  const dayNames = texts.dayNames || ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  const [currentStep, setCurrentStep] = useState(0);
  const [timeslots, setTimeslots] = useState<TimeSlot[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState<string>('');
  const [selectedSlot, setSelectedSlot] = useState<TimeSlot | null>(null);
  const [calendarMonth, setCalendarMonth] = useState<string>('');
  const [formData, setFormData] = useState({
    customer_name: '',
    customer_phone: '',
    notes: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const fetchTimeslots = async () => {
    setLoading(true);
    try {
      const response = await fetch(timeslotsApi);
      const data = await response.json();
      setTimeslots(data || []);
      
      if (data && data.length > 0) {
        const today = new Date().toISOString().split('T')[0];
        const availableDates = data.map((slot: TimeSlot) => slot.start_time.split(' ')[0]);
        const uniqueDates = Array.from(new Set(availableDates)).sort();
        
        let closestDate = uniqueDates.find((date: string) => date >= today);
        if (!closestDate) {
          closestDate = uniqueDates[uniqueDates.length - 1];
        }
        
        setSelectedDate(closestDate);
        setCalendarMonth(closestDate.substring(0, 7));
      }
    } catch (error) {
      console.error('Failed to fetch timeslots:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTimeslots();
  }, [timeslotsApi]);

  const getAvailableDates = () => {
    const dates = timeslots.map(slot => slot.start_time.split(' ')[0]);
    return Array.from(new Set(dates));
  };

  const getSlotsForDate = (date: string) => {
    return timeslots.filter(slot => slot.start_time.split(' ')[0] === date);
  };

  const generateCalendarDays = () => {
    if (!calendarMonth) return [];
    
    const [year, month] = calendarMonth.split('-').map(Number);
    const firstDay = new Date(year, month - 1, 1);
    const lastDay = new Date(year, month, 0);
    const startDayOfWeek = firstDay.getDay();
    const daysInMonth = lastDay.getDate();
    
    const days: (string | null)[] = [];
    
    for (let i = 0; i < startDayOfWeek; i++) {
      days.push(null);
    }
    
    for (let day = 1; day <= daysInMonth; day++) {
      const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      days.push(dateStr);
    }
    
    return days;
  };

  const navigateMonth = (direction: number) => {
    if (!calendarMonth) return;
    
    const [year, month] = calendarMonth.split('-').map(Number);
    const newDate = new Date(year, month - 1 + direction, 1);
    const newMonth = `${newDate.getFullYear()}-${String(newDate.getMonth() + 1).padStart(2, '0')}`;
    setCalendarMonth(newMonth);
  };

  const handleDateSelect = (date: string) => {
    const availableDates = getAvailableDates();
    if (availableDates.includes(date)) {
      setSelectedDate(date);
      setCurrentStep(1);
    }
  };

  const handleSlotSelect = (slot: TimeSlot) => {
    setSelectedSlot(slot);
    setCurrentStep(2);
  };

  const handleFormChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    if (!selectedSlot || !formData.customer_name || !formData.customer_phone) {
      setMessage({ type: 'error', text: texts.bookingError || 'Please fill in all required fields' });
      return;
    }

    setSubmitting(true);
    setMessage(null);

    try {
      const response = await fetch(appointmentsApi, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          customer_name: formData.customer_name,
          customer_phone: formData.customer_phone,
          start_time: selectedSlot.start_time,
          end_time: selectedSlot.end_time,
          notes: formData.notes
        })
      });

      if (response.ok) {
        setMessage({ type: 'success', text: texts.bookingSuccess || 'Booking successful!' });
        await fetchTimeslots();
        setSelectedDate('');
        setSelectedSlot(null);
        setFormData({ customer_name: '', customer_phone: '', notes: '' });
        setCurrentStep(0);
      } else {
        setMessage({ type: 'error', text: texts.bookingError || 'Booking failed. Please try again.' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: texts.bookingError || 'Booking failed. Please try again.' });
    } finally {
      setSubmitting(false);
    }
  };

  const formatTime = (datetime: string) => {
    const timePart = datetime.split(' ')[1];
    if (!timePart) return '';
    const [hourStr, minuteStr] = timePart.split(':');
    let hour = parseInt(hourStr, 10);
    const minute = minuteStr || '00';
    const ampm = hour >= 12 ? 'PM' : 'AM';
    hour = hour % 12;
    if (hour === 0) hour = 12;
    return `${hour}:${minute} ${ampm}`;
  };

  const availableDates = getAvailableDates();
  const calendarDays = generateCalendarDays();
  const slotsForSelectedDate = selectedDate ? getSlotsForDate(selectedDate) : [];

  if (loading) {
    return (
      <div className={`flex items-center justify-center py-12 ${containerStyle}`}>
        <div className="w-8 h-8 border-4 border-yellow-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className={containerStyle}>
      {message && (
        <div className={`mb-6 p-4 rounded-lg ${message.type === 'success' ? 'bg-green-500/20 text-green-300 border border-green-500/40' : 'bg-red-500/20 text-red-300 border border-red-500/40'}`}>
          {message.text}
        </div>
      )}

      {currentStep === 0 && (
        <div>
          <h3 className="text-xl sm:text-2xl font-bold text-white mb-6">{texts.selectDate || 'Select a Date'}</h3>
          
          <div className="bg-slate-800/50 rounded-xl p-3 sm:p-6 border border-yellow-500/20">
            <div className="flex items-center justify-between mb-4 sm:mb-6">
              <button
                onClick={() => navigateMonth(-1)}
                className="w-10 h-10 flex items-center justify-center rounded-lg bg-slate-700 hover:bg-slate-600 transition-colors cursor-pointer"
              >
                <i className="ri-arrow-left-s-line text-white text-xl"></i>
              </button>
              
              <div className="text-base sm:text-lg font-bold text-white">
                {calendarMonth && `${monthNames[parseInt(calendarMonth.split('-')[1]) - 1]} ${calendarMonth.split('-')[0]}`}
              </div>
              
              <button
                onClick={() => navigateMonth(1)}
                className="w-10 h-10 flex items-center justify-center rounded-lg bg-slate-700 hover:bg-slate-600 transition-colors cursor-pointer"
              >
                <i className="ri-arrow-right-s-line text-white text-xl"></i>
              </button>
            </div>

            <div className="grid grid-cols-7 gap-1 sm:gap-2 mb-1 sm:mb-2">
              {dayNames.map(day => (
                <div key={day} className="text-center text-xs sm:text-sm font-semibold text-yellow-400 py-1 sm:py-2 truncate">
                  <span className="hidden sm:inline">{day}</span>
                  <span className="sm:hidden">{day.charAt(0)}</span>
                </div>
              ))}
            </div>

            <div className="grid grid-cols-7 gap-1 sm:gap-2">
              {calendarDays.map((date, index) => {
                if (!date) {
                  return <div key={`empty-${index}`} className="aspect-square"></div>;
                }
                
                const isAvailable = availableDates.includes(date);
                const isSelected = date === selectedDate;
                const dayOfMonth = date.split('-')[2];
                
                return (
                  <button
                    key={date}
                    onClick={() => isAvailable && handleDateSelect(date)}
                    disabled={!isAvailable}
                    className={`aspect-square rounded-lg flex items-center justify-center text-xs sm:text-sm font-medium transition-all cursor-pointer ${
                      isSelected
                        ? 'bg-yellow-500 text-black'
                        : isAvailable
                        ? 'bg-slate-700 text-white hover:bg-slate-600'
                        : 'bg-slate-900/50 text-slate-600 cursor-not-allowed'
                    }`}
                  >
                    {parseInt(dayOfMonth, 10)}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {currentStep === 1 && (
        <div>
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl sm:text-2xl font-bold text-white">{texts.selectTime || 'Select a Time'}</h3>
            <button
              onClick={() => setCurrentStep(0)}
              className="text-yellow-400 hover:text-yellow-300 text-sm font-medium cursor-pointer whitespace-nowrap"
            >
              <i className="ri-arrow-left-line mr-1"></i>
              {texts.back || 'Back'}
            </button>
          </div>

          <div className="bg-slate-800/50 rounded-xl p-4 sm:p-6 border border-yellow-500/20 mb-4">
            <div className="text-sm text-yellow-400 mb-1">{texts.selectedDate || 'Selected Date'}</div>
            <div className="text-base sm:text-lg font-bold text-white">{selectedDate}</div>
          </div>

          {slotsForSelectedDate.length === 0 ? (
            <div className="text-center py-8 text-slate-400">
              {texts.noSlotsAvailable || 'No time slots available for this date'}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {slotsForSelectedDate.map((slot, index) => (
                <button
                  key={index}
                  onClick={() => handleSlotSelect(slot)}
                  className="bg-slate-800/50 hover:bg-slate-700/50 border border-yellow-500/20 hover:border-yellow-500/40 rounded-lg p-4 text-left transition-all cursor-pointer"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 flex items-center justify-center bg-yellow-500/20 rounded-lg flex-shrink-0">
                      <i className="ri-time-line text-yellow-400 text-xl"></i>
                    </div>
                    <div>
                      <div className="text-white font-semibold text-sm sm:text-base">
                        {formatTime(slot.start_time)} - {formatTime(slot.end_time)}
                      </div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {currentStep === 2 && selectedSlot && (
        <div>
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl sm:text-2xl font-bold text-white">{texts.reviewBooking || 'Review & Confirm'}</h3>
          </div>

          <div className="bg-slate-800/50 rounded-xl p-4 sm:p-6 border border-yellow-500/20 mb-6 space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <div>
                <div className="text-sm text-yellow-400 mb-1">{texts.selectedDate || 'Selected Date'}</div>
                <div className="text-base sm:text-lg font-bold text-white">{selectedDate}</div>
              </div>
              <button
                onClick={() => setCurrentStep(0)}
                className="text-yellow-400 hover:text-yellow-300 text-sm font-medium cursor-pointer whitespace-nowrap self-start sm:self-auto"
              >
                {texts.modifyTime || 'Modify Date'}
              </button>
            </div>
            <div className="border-t border-white/10 pt-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <div>
                <div className="text-sm text-yellow-400 mb-1">{texts.selectedTime || 'Selected Time'}</div>
                <div className="text-base sm:text-lg font-bold text-white">
                  {formatTime(selectedSlot.start_time)} - {formatTime(selectedSlot.end_time)}
                </div>
              </div>
              <button
                onClick={() => setCurrentStep(1)}
                className="text-yellow-400 hover:text-yellow-300 text-sm font-medium cursor-pointer whitespace-nowrap self-start sm:self-auto"
              >
                Modify Time
              </button>
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-white mb-2">
                {texts.customerName || 'Name'} <span className="text-red-400">*</span>
              </label>
              <input
                type="text"
                value={formData.customer_name}
                onChange={(e) => handleFormChange('customer_name', e.target.value)}
                className="w-full px-4 py-3 bg-slate-800/50 border border-yellow-500/20 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-yellow-500/40"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-white mb-2">
                {texts.customerPhone || 'Phone'} <span className="text-red-400">*</span>
              </label>
              <input
                type="tel"
                value={formData.customer_phone}
                onChange={(e) => handleFormChange('customer_phone', e.target.value)}
                className="w-full px-4 py-3 bg-slate-800/50 border border-yellow-500/20 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-yellow-500/40"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-white mb-2">
                {texts.notes || 'Notes (Optional)'}
              </label>
              <textarea
                value={formData.notes}
                onChange={(e) => handleFormChange('notes', e.target.value)}
                rows={4}
                className="w-full px-4 py-3 bg-slate-800/50 border border-yellow-500/20 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-yellow-500/40 resize-none"
              />
            </div>

            <button
              onClick={handleSubmit}
              disabled={submitting || !formData.customer_name || !formData.customer_phone}
              className="w-full px-6 py-4 bg-yellow-500 text-black rounded-lg font-bold text-base hover:bg-yellow-400 transition-all disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
            >
              {submitting ? (
                <span className="flex items-center justify-center gap-2">
                  <div className="w-5 h-5 border-3 border-black border-t-transparent rounded-full animate-spin"></div>
                  {texts.submit || 'Submitting...'}
                </span>
              ) : (
                texts.submit || 'Confirm Booking'
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}